import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import { Tag, TagGroup } from '@plone/components';
import { ArrowrightIcon, UserIcon } from '@plone/components/Icons';
import { ESSButton } from '../../Atoms/ESSButton';
import RichText from '../../Atoms/RichText';
import './TeaserCardVertical.scss';
import './../../../theme/_ess-design-system.scss';

const getCardStyle = ({ width, border, bgColor }) => {
  const style = {
    ...(border && { border }),
    ...(bgColor && { backgroundColor: bgColor }),
  };

  if (!width) {
    return style;
  }

  if (typeof width === 'string') {
    style.width = width;
    style.maxWidth = width;
    return style;
  }

  style['--teaser-card-vertical-width-xs'] = width.xs || '100%';
  style['--teaser-card-vertical-width-sm'] = width.sm || width.xs || '100%';
  style['--teaser-card-vertical-width-md'] =
    width.md || width.sm || width.xs || '100%';
  style['--teaser-card-vertical-width-lg'] =
    width.lg || width.md || width.sm || width.xs || '100%';
  style['--teaser-card-vertical-width-xl'] =
    width.xl || width.lg || width.md || width.sm || width.xs || '100%';
  style['--teaser-card-vertical-width-xxl'] =
    width.xxl || width.xl || width.lg || width.md || width.sm || width.xs || '100%';

  return style;
};

const TeaserCardVertical = ({
  title,
  description,
  imageUrl,
  url,
  tags,
  label,
  labelHeight,
  labelBgColor,
  labelFgColor,
  bgColor = 'var(--teasercardstandard-variantdefault-statedefault-bgcolor)',
  border,
  previewBorder,
  caption,
  readMoreText,
  className,
  isEditMode,
  fitImage,
  showCTA,
  width,
}) => {
  const intl = useIntl();
  const internalUrl = url ? flattenToAppURL(url) : null;
  const cardStyle = getCardStyle({ width, border, bgColor });
  const cardContent = (
    <>
      <div
        className="teaser-card__media"
        style={{
          ...((title?.trim() || description?.trim()) && {
            borderBottom: previewBorder,
          }),
        }}
      >
        {label && (
          <div
            className="teaser-card__label"
            style={{
              ...(labelBgColor && { backgroundColor: labelBgColor }),
              ...(labelFgColor && { color: labelFgColor }),
              ...(labelHeight && { height: labelHeight }),
              minHeight: '1.7rem',
            }}
          >
            {label}
          </div>
        )}
        {imageUrl && (
          <img
            className={
              fitImage ? 'teaser-card__media--fit' : 'teaser-card__media--cover'
            }
            src={flattenToAppURL(
              typeof imageUrl === 'string'
                ? imageUrl
                : imageUrl.download || imageUrl.url || '',
            )}
            alt={title || ''}
            loading="lazy"
          />
        )}
      </div>
      <div className="teaser-card__content">
        {caption && <span className="teaser-card__caption">{caption}</span>}
        {title && (
          <h4 className="teaser-card__title">
            <RichText content={title} withVerticalSpacing={false} />
          </h4>
        )}
        {description && (
          <RichText
            className="teaser-card__description"
            content={description}
          />
        )}
        {tags.length > 0 && (
          <TagGroup
            onSelectionChange={() => {}}
            selectionMode="none"
            aria-label="Item tags"
            className="teaser-card__tags"
          >
            {tags.map((tag) => (
              <Tag key={tag} itemKey={tag}>
                {tag}
              </Tag>
            ))}
          </TagGroup>
        )}
      </div>
      {internalUrl && !isEditMode && showCTA && (
        <ESSButton
          radius="none"
          variant="positive"
          size="md"
          fullWidth={true}
          className="teaser-card__read-more"
          href={internalUrl}
        >
          {readMoreText || 'Explore'}
          <ArrowrightIcon size="20px" className="ml-2" />
        </ESSButton>
      )}
    </>
  );

  return (
    <>
      {internalUrl ? (
        <UniversalLink
          href={internalUrl}
          className={`teaser-card teaser-card-vertical ${className}`.trim()}
          style={cardStyle}
        >
          {cardContent}
        </UniversalLink>
      ) : (
        <div
          className={`teaser-card teaser-card-vertical ${className}`.trim()}
          style={cardStyle}
        >
          {cardContent}
        </div>
      )}
    </>
  );
};

TeaserCardVertical.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  url: PropTypes.string,
  tags: PropTypes.array,
  label: PropTypes.string,
  labelHeight: PropTypes.string,
  labelBgColor: PropTypes.string,
  labelFgColor: PropTypes.string,
  bgColor: PropTypes.string,
  border: PropTypes.string,
  previewBorder: PropTypes.string,
  caption: PropTypes.string,
  readMoreText: PropTypes.string,
  className: PropTypes.string,
  isEditMode: PropTypes.bool,
  fitImage: PropTypes.bool,
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      xs: PropTypes.string,
      sm: PropTypes.string,
      md: PropTypes.string,
      lg: PropTypes.string,
      xl: PropTypes.string,
      xxl: PropTypes.string,
    }),
  ]),
};

TeaserCardVertical.defaultProps = {
  tags: [],
  border: 'var(--teasercardstandard-variantdefault-statedefault-border)',
  previewBorder:
    'var(--teasercardstandard-variantdefault-statedefault-preview-border)',
  className: '',
  isEditMode: false,
  fitImage: false,
  showCTA: true,
};

export default TeaserCardVertical;
